import * as React from "react";
import {
    Button,
    Card,
    Checkbox,
    Dimmer,
    Dropdown,
    Header,
    Icon,
    Label,
    Loader,
    Segment,
} from "semantic-ui-react";
import { notify } from "react-notify-toast";
import {
    fetchScoredUnassignedTickets,
    analyzeTicketForPickup,
    recommendTopTicketsForPickup,
    ScoredTicket,
    AnalyzeTicketForPickupResponse,
    TopTicketRecommendation,
} from "./GitlabTicketsApi";

export function ScoredUnassignedTicketPicker() {
    const [tickets, setTickets] = React.useState<ScoredTicket[]>([]);
    const [includedLabels, setIncludedLabels] = React.useState<string[]>([]);
    const [excludedLabels, setExcludedLabels] = React.useState<string[]>([]);
    const [includeGenericAi, setIncludeGenericAi] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [analyzingTicketId, setAnalyzingTicketId] = React.useState<number | null>(null);
    const [recommending, setRecommending] = React.useState(false);
    const [topRecommendations, setTopRecommendations] = React.useState<TopTicketRecommendation[]>([]);
    const [analysisByTicketId, setAnalysisByTicketId] = React.useState<
        Record<number, AnalyzeTicketForPickupResponse>
    >({});

    // Labels used purely for the scoring system are noise for the filter / display.
    const isScoreLabel = (label: string) => /^score::/i.test(label);

    // All distinct (non-score) labels across the loaded tickets, used as dropdown options.
    const availableLabels = React.useMemo(() => {
        const set = new Set<string>();
        tickets.forEach((ticket) =>
            (ticket.labels ?? []).filter((l) => !isScoreLabel(l)).forEach((l) => set.add(l))
        );
        return Array.from(set).sort((a, b) => a.localeCompare(b));
    }, [tickets]);

    const labelOptions = availableLabels.map((label) => ({
        key: label,
        text: label,
        value: label,
    }));

    // Frontend-only filter:
    // - keep tickets that carry every included label
    // - remove tickets that carry any excluded label
    const filteredTickets = React.useMemo(() => {
        return tickets.filter((ticket) => {
            const ticketLabels = ticket.labels ?? [];
            const matchesIncludedLabels = includedLabels.every((included) =>
                ticketLabels.includes(included)
            );
            const matchesExcludedLabels = excludedLabels.some((excluded) =>
                ticketLabels.includes(excluded)
            );
            return matchesIncludedLabels && !matchesExcludedLabels;
        });
    }, [tickets, includedLabels, excludedLabels]);

    const normalizeGitlabColor = (value?: string): string | undefined => {
        if (!value) return undefined;
        const trimmed = value.trim();
        if (!trimmed) return undefined;
        if (/^#/.test(trimmed) || /^rgb/i.test(trimmed) || /^hsl/i.test(trimmed)) {
            return trimmed;
        }
        if (/^[0-9a-f]{3,8}$/i.test(trimmed)) {
            return `#${trimmed}`;
        }
        return undefined;
    };

    const gitlabLabelStyleByName = React.useMemo(() => {
        const styleByName = new Map<string, React.CSSProperties>();
        tickets.forEach((ticket) => {
            (ticket.labelDetails ?? []).forEach((label) => {
                const name = label.name?.trim();
                if (!name || styleByName.has(name)) {
                    return;
                }
                const backgroundColor = normalizeGitlabColor(label.color);
                const textColor = normalizeGitlabColor(label.textColor);
                if (!backgroundColor && !textColor) {
                    return;
                }
                styleByName.set(name, {
                    backgroundColor,
                    color: textColor,
                    border: `1px solid ${textColor ?? "rgba(34, 36, 38, 0.25)"}`,
                });
            });
        });
        return styleByName;
    }, [tickets]);

    const loadTickets = async (includeGenericAiOverride?: boolean) => {
        setLoading(true);
        try {
            const include = includeGenericAiOverride ?? includeGenericAi;
            const response = await fetchScoredUnassignedTickets(include);
            setTickets(response.tickets ?? []);
            setIncludedLabels([]);
            setExcludedLabels([]);
            notify.show("Scored unassigned tickets loaded", "success", 3000);
        } catch (error) {
            console.error("Error loading scored tickets:", error);
            notify.show(
                error instanceof Error ? error.message : "Failed to fetch tickets",
                "error",
                5000
            );
        } finally {
            setLoading(false);
        }
    };

    const handleRecommendTop3 = async () => {
        if (filteredTickets.length === 0) return;
        setRecommending(true);
        setTopRecommendations([]);
        try {
            const response = await recommendTopTicketsForPickup(filteredTickets);
            setTopRecommendations(response.recommendations ?? []);
            notify.show("Top 3 recommendations ready", "success", 3000);
        } catch (error) {
            console.error("Error getting recommendations:", error);
            notify.show(
                error instanceof Error ? error.message : "Recommendation failed",
                "error",
                5000
            );
        } finally {
            setRecommending(false);
        }
    };

    const handleAnalyze = async (ticket: ScoredTicket) => {
        setAnalyzingTicketId(ticket.ticketId);
        try {
            const response = await analyzeTicketForPickup({
                ticketId: ticket.ticketId,
                ticketName: ticket.ticketName,
                ticketText: ticket.ticketText,
                score: ticket.score,
                labels: ticket.labels,
            });
            setAnalysisByTicketId((prev) => ({ ...prev, [ticket.ticketId]: response }));
        } catch (error) {
            console.error("Error analyzing ticket:", error);
            notify.show(
                error instanceof Error ? error.message : "Analysis failed",
                "error",
                5000
            );
        } finally {
            setAnalyzingTicketId(null);
        }
    };

    const getDiffColor = (diff: string) => {
        switch (diff) {
            case "LOW":
                return "green";
            case "MEDIUM":
                return "yellow";
            case "HIGH":
                return "red";
            default:
                return "grey";
        }
    };

    if (loading) {
        return (
            <Segment>
                <Dimmer active>
                    <Loader size="large">Loading from GitLab…</Loader>
                </Dimmer>
            </Segment>
        );
    }

    return (
        <div style={{ padding: "20px" }}>
            <Segment compact>
                <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "16px", flexWrap: "wrap" }}>
                    <Icon name="gitlab" />
                    <Header as="h4" style={{ margin: 0 }}>
                        Unassigned Scored Tickets
                    </Header>
                    <Label color="grey" size="small">
                        Sorted by score (highest first)
                    </Label>
                    <Checkbox
                        toggle
                        label="Include GenericAi"
                        checked={includeGenericAi}
                        onChange={(_, data) => {
                            const next = data.checked === true;
                            setIncludeGenericAi(next);
                            if (tickets.length > 0) {
                                setTopRecommendations([]);
                                loadTickets(next);
                            }
                        }}
                    />
                    <Button primary onClick={() => loadTickets()} icon="sync" content="Fetch Tickets" />
                    {
                    <Button
                        color="green"
                        onClick={handleRecommendTop3}
                        loading={recommending}
                        disabled={filteredTickets.length === 0 || recommending}
                        icon="star"
                        content="Recommend Top 3"
                    />
                    }
                </div>
                {tickets.length > 0 && (
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                        <Icon name="tags" />
                        <Dropdown
                            placeholder="Include labels"
                            multiple
                            selection
                            search
                            clearable
                            options={labelOptions}
                            value={includedLabels}
                            onChange={(_, data) => setIncludedLabels((data.value as string[]) ?? [])}
                            style={{ minWidth: "260px" }}
                        />
                        <Dropdown
                            placeholder="Exclude labels"
                            multiple
                            selection
                            search
                            clearable
                            options={labelOptions}
                            value={excludedLabels}
                            onChange={(_, data) => setExcludedLabels((data.value as string[]) ?? [])}
                            style={{ minWidth: "260px" }}
                        />
                        <Label basic size="small">
                            {filteredTickets.length} / {tickets.length} shown
                        </Label>
                    </div>
                )}
            </Segment>

            {topRecommendations.length > 0 && (
                <Segment color="green" style={{ marginBottom: "20px", borderLeft: "4px solid #21ba45" }}>
                    <Header as="h5" style={{ marginBottom: "12px" }}>
                        <Icon name="star" /> Top 3 picks (by score, difficulty, impact)
                    </Header>
                    {topRecommendations.map((rec) => {
                        const ticket = tickets.find((t) => t.ticketId === rec.ticketId);
                        return (
                            <Card key={rec.ticketId} fluid style={{ marginBottom: "12px" }}>
                                <Card.Content>
                                    <Label color="blue" size="small">
                                        #{rec.rank}
                                    </Label>
                                    <Card.Header>
                                        <a
                                            href={ticket?.webUrl ?? "#"}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            #{ticket?.iid ?? rec.ticketId} {ticket?.ticketName ?? "Ticket"}
                                        </a>
                                    </Card.Header>
                                    <Label color={getDiffColor(rec.difficultyAssessment) as any} size="small">
                                        {rec.difficultyAssessment}
                                    </Label>
                                    <p style={{ margin: "8px 0 0 0", fontWeight: 600 }}>{rec.suggestion}</p>
                                    <p style={{ margin: "8px 0 0 0", fontSize: "0.9em" }}>{rec.reasoning}</p>
                                </Card.Content>
                            </Card>
                        );
                    })}
                </Segment>
            )}

            {tickets.length === 0 ? (
                <Segment placeholder>
                    <Header icon>
                        <Icon name="ticket" />
                        No unassigned scored tickets
                    </Header>
                    <Button primary onClick={() => loadTickets()}>
                        Fetch from GitLab
                    </Button>
                </Segment>
            ) : filteredTickets.length === 0 ? (
                <Segment placeholder>
                    <Header icon>
                        <Icon name="filter" />
                        No tickets match the selected include/exclude labels
                    </Header>
                    <Button
                        onClick={() => {
                            setIncludedLabels([]);
                            setExcludedLabels([]);
                        }}
                    >
                        Clear label filters
                    </Button>
                </Segment>
            ) : (
                <Card.Group itemsPerRow={1}>
                    {filteredTickets.map((ticket) => {
                        const analysis = analysisByTicketId[ticket.ticketId];
                        const isAnalyzing = analyzingTicketId === ticket.ticketId;
                        return (
                            <Card key={ticket.ticketId}>
                                <Card.Content>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                                        <div style={{ flex: 1 }}>
                                            <Card.Header>
                                                <a href={ticket.webUrl ?? "#"} target="_blank" rel="noopener noreferrer">
                                                    #{ticket.iid} {ticket.ticketName ?? "(no title)"}
                                                </a>
                                            </Card.Header>
                                            <Card.Meta>
                                                <Label color="blue" size="small">
                                                    Score: {ticket.score}
                                                </Label>
                                                {(ticket.labels ?? [])
                                                    .filter((l) => !isScoreLabel(l))
                                                    .map((l) => {
                                                        const active = includedLabels.includes(l);
                                                        const gitlabStyle = gitlabLabelStyleByName.get(l);
                                                        const fallbackColor = !gitlabStyle && active ? "teal" : undefined;
                                                        const style: React.CSSProperties = gitlabStyle
                                                            ? {
                                                                  ...gitlabStyle,
                                                                  cursor: "pointer",
                                                                  fontWeight: active ? 700 : undefined,
                                                                  boxShadow: active
                                                                      ? "0 0 0 1px rgba(33, 133, 208, 0.35)"
                                                                      : undefined,
                                                              }
                                                            : { cursor: "pointer" };
                                                        return (
                                                            <Label
                                                                key={l}
                                                                size="mini"
                                                                color={fallbackColor}
                                                                style={style}
                                                                onClick={() =>
                                                                    setIncludedLabels((prev) =>
                                                                        prev.includes(l)
                                                                            ? prev.filter((x) => x !== l)
                                                                            : [...prev, l]
                                                                    )
                                                                }
                                                            >
                                                                {l}
                                                            </Label>
                                                        );
                                                    })}
                                            </Card.Meta>
                                            {analysis && (
                                                <Segment style={{ marginTop: "12px" }}>
                                                    <Label color={getDiffColor(analysis.difficultyAssessment) as any}>
                                                        {analysis.difficultyAssessment}
                                                    </Label>
                                                    <p style={{ margin: "8px 0 0 0", fontWeight: 600 }}>
                                                        {analysis.suggestion}
                                                    </p>
                                                    <p style={{ margin: "8px 0 0 0", fontSize: "0.9em" }}>
                                                        {analysis.reasoning}
                                                    </p>
                                                    {analysis.recommendedOrder && (
                                                        <p style={{ margin: "8px 0 0 0", fontStyle: "italic" }}>
                                                            {analysis.recommendedOrder}
                                                        </p>
                                                    )}
                                                </Segment>
                                            )}
                                        </div>
                                        {
                                        <Button
                                            secondary
                                            loading={isAnalyzing}
                                            disabled={isAnalyzing}
                                            onClick={() => handleAnalyze(ticket)}
                                        >
                                            Analyze
                                        </Button>
                                        }
                                    </div>
                                </Card.Content>
                            </Card>
                        );
                    })}
                </Card.Group>
            )}
        </div>
    );
}
